/**
 * Zoony shared color/image URL matcher.
 * Handles Shopify filename de-duplication suffixes and loose URL formats.
 *
 * Store-wide Files naming (Shopify has one image folder for the whole shop):
 *   {color}_{shot}_{product-key}.jpg     e.g. black_1_17_pro, black_1_17_pro_max
 *   {general|gen}_{shot}_{product-key}.jpg e.g. general_1_17_pro
 * Color option values stay "Black" (etc). Do not put the product key in the variant name.
 * Attach each file only to that product — do not reuse the other product's Files entry.
 */
(function () {
  'use strict';

  function normalizeText(value) {
    return decodeURIComponent(String(value || ''))
      .toLowerCase()
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function compactText(value) {
    return normalizeText(value).replace(/\s+/g, '');
  }

  function tokens(value) {
    var t = normalizeText(value).split(' ');
    var out = [];
    for (var i = 0; i < t.length; i++) {
      if (t[i]) out.push(t[i]);
    }
    return out;
  }

  function getPathStem(url) {
    var clean = decodeURIComponent(String(url || '').split('?')[0]);
    var last = clean.split('/').pop() || '';
    return last.replace(/\.[a-z0-9]+$/i, '');
  }

  // Handles classic `color_12` and renamed `color_12_<uuid>` forms.
  function extractLegacyColorStem(url) {
    var stem = getPathStem(url);
    if (!stem) return '';
    var normalized = normalizeText(stem);
    var m = normalized.match(/^(.+?)\s\d+(?:\s[a-f0-9-]{8,})?$/i);
    if (m && m[1]) return normalizeText(m[1]);
    return '';
  }

  function hasBoundaryToken(haystack, token) {
    var escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var re = new RegExp('(^|[^a-z0-9])' + escaped + '([^a-z0-9]|$)', 'i');
    return re.test(haystack);
  }

  function conflictGuard(urlNorm, colorNorm) {
    if (colorNorm === 'blue' && (urlNorm.indexOf('baby blue') !== -1 || urlNorm.indexOf('babyblue') !== -1)) return false;
    return true;
  }

  function urlMatchesColor(url, colorOptionValue) {
    var colorNorm = normalizeText(colorOptionValue);
    if (!colorNorm) return false;

    // Shared "general" gallery shots are not a color.
    if (urlMatchesGeneral(url) && colorNorm !== 'general' && colorNorm !== 'gen') return false;

    var urlNorm = normalizeText(getPathStem(url) + ' ' + decodeURIComponent(String(url || '')));
    if (!conflictGuard(urlNorm, colorNorm)) return false;

    // 1) Fast legacy match
    var legacy = extractLegacyColorStem(url);
    if (legacy && legacy === colorNorm) return true;

    // 2) Compact phrase match (e.g. navy blue -> navyblue)
    if (compactText(urlNorm).indexOf(compactText(colorNorm)) !== -1) return true;

    // 3) Token boundary match
    var colorTokens = tokens(colorNorm);
    for (var i = 0; i < colorTokens.length; i++) {
      if (!hasBoundaryToken(urlNorm, colorTokens[i])) return false;
    }
    return colorTokens.length > 0;
  }

  /**
   * Shared photos for every color: general_1, gen_1, general_1_17_pro, etc.
   * First token must be general/gen; shot index, Shopify UUID, and product-key
   * suffix (17_pro, 17_pro_max) are all allowed after that.
   */
  function urlMatchesGeneral(url) {
    if (!url) return false;
    var legacy = extractLegacyColorStem(url);
    if (legacy === 'general' || legacy === 'gen') return true;

    var stem = normalizeText(getPathStem(url));
    if (!stem) return false;
    if (stem === 'general' || stem === 'gen') return true;

    var stemTokens = tokens(stem);
    if (stemTokens.length === 0) return false;
    return stemTokens[0] === 'general' || stemTokens[0] === 'gen';
  }

  /**
   * Shot number from {color}_{shot}_{product-key} (black_1_17_pro → 1).
   * First numeric token after the color/general prefix. Null if none.
   */
  function urlShotIndex(url) {
    var stem = normalizeText(getPathStem(url));
    if (!stem) return null;
    var stemTokens = tokens(stem);
    for (var i = 1; i < stemTokens.length; i++) {
      if (/^\d+$/.test(stemTokens[i])) return parseInt(stemTokens[i], 10);
    }
    return null;
  }

  window.zoonyColorImageMatch = {
    normalizeColor: normalizeText,
    extractLegacyColorStem: extractLegacyColorStem,
    urlMatchesColor: urlMatchesColor,
    urlMatchesGeneral: urlMatchesGeneral,
    urlShotIndex: urlShotIndex
  };
})();
